import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthStatusScreen } from "@/components/auth/AuthStatusScreen";
import { useAuth } from "@/contexts/AuthContext";

export function VerifyPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { verifyAndLogin, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState<
    "pending" | "success" | "error" | "usePassword" | "noAccount"
  >("pending");
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      toast.error(t("auth.verify.error"));
      setStatus("error");
      return;
    }
    if (isLoading || isAuthenticated) return;
    if (startedRef.current) return;
    startedRef.current = true;

    verifyAndLogin(token)
      .then(() => {
        if (mountedRef.current) {
          toast.success(t("auth.verify.successToast"));
          setStatus("success");
        }
      })
      .catch((err: { response?: { status?: number } }) => {
        if (mountedRef.current) {
          if (err?.response?.status === 403) {
            setStatus("usePassword");
          } else if (err?.response?.status === 404) {
            toast.error(t("auth.noAccountForEmail"));
            setStatus("noAccount");
          } else {
            toast.error(t("auth.verify.error"));
            setStatus("error");
          }
        }
      });
  }, [token, verifyAndLogin, isLoading, isAuthenticated]);

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (status === "success") {
    return <Navigate to="/" replace />;
  }

  if (status === "usePassword") {
    return (
      <AuthStatusScreen
        state="info"
        title={t("auth.verify.usePasswordTitle", "Iniciá con contraseña")}
        message={t("auth.verify.usePassword")}
        action={
          <Link to="/login" className="fx-auth-btn fx-auth-btn-primary">
            {t("auth.verify.usePasswordLink")}
          </Link>
        }
      />
    );
  }

  if (status === "noAccount") {
    return (
      <AuthStatusScreen
        state="info"
        title={t("auth.verify.noAccountTitle", "Todavía no tenés cuenta")}
        message={t("auth.noAccountForEmail")}
        action={
          <>
            <Link to="/login" className="fx-auth-btn fx-auth-btn-ghost">
              {t("pages.common.back")}
            </Link>
            <Link to="/register" className="fx-auth-btn fx-auth-btn-primary">
              {t("auth.login.createNewAccount")}
            </Link>
          </>
        }
      />
    );
  }

  if (status === "error") {
    return (
      <AuthStatusScreen
        state="error"
        title={t("auth.verify.errorTitle", "No pudimos verificar el enlace")}
        message={t("auth.verify.error")}
        action={
          <Link to="/login" className="fx-auth-btn fx-auth-btn-primary">
            {t("pages.common.back")}
          </Link>
        }
      />
    );
  }

  return (
    <AuthStatusScreen
      state="loading"
      title={t("auth.verify.pendingTitle", "Verificando tu enlace")}
      message={t(
        "auth.verify.pendingDetail",
        "Estamos confirmando tu acceso. Esto toma un segundo.",
      )}
    />
  );
}
